import * as am5 from '@amcharts/amcharts5';
import am5lang_pt_BR from '@amcharts/amcharts5/locales/pt_BR';
import * as am5plugins_exporting from '@amcharts/amcharts5/plugins/exporting';
import am5themes_animated from '@amcharts/amcharts5/themes/Animated';
import * as am5xy from '@amcharts/amcharts5/xy';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { classificarColunaHistoricoIdss, TIPO_COLUNA_HISTORICO_IDSS_LABEL } from '@gestor-web/@core/api/ords/model';

const CHART_ID_PREFIX = 'historico-idss-chart';
const TITLE_PREFIX = 'Histórico do IDSS';

@Component({
    selector: 'bl-historico-idss-chart',
    template: `<div class="historico-idss-chart" [id]="chartId"></div>`,
    styleUrls: ['./historico-idss-chart.component.scss'],
    standalone: false
})
export class HistoricoIdssChartComponent implements AfterViewInit, OnDestroy {
  private titleText: string;
  private filenameValue: string;

  private _root: am5.Root;
  private _chart: am5xy.XYChart;
  private _title: am5.Label;
  private _export: am5plugins_exporting.Exporting;

  private _axis: am5xy.Axis<am5xy.AxisRenderer>[] = [];
  private _series: am5xy.XYSeries[] = [];

  private _rangeSip: am5.DataItem<am5xy.ICategoryAxisDataItem>;
  private _rangeTiss: am5.DataItem<am5xy.ICategoryAxisDataItem>;

  chartId: string;

  @Output() simuladoClick = new EventEmitter<{ cd_simulado: number; tp_simulado: string; nr_ano_base?: any }>();

  private _pendingData: any[] | null = null;

  constructor() {
    this.chartId = CHART_ID_PREFIX + '_' + Math.floor(Math.random() * 1000);
  }

  ngAfterViewInit(): void {
    am5.ready(() => {
      // Root Element
      const self = this;
      const root = am5.Root.new(this.chartId);
      root.setThemes([am5themes_animated.new(root)]);
      root.numberFormatter.set('numberFormat', '#.####'); // remove '.' dos Anos.
      root.locale = am5lang_pt_BR;

      //reset do cursor
      root.dom.addEventListener('mouseleave', () => {
        (root.dom as HTMLElement).style.cursor = 'default';
      });

      // Export
      const exporting = am5plugins_exporting.Exporting.new(root, {
        menu: am5plugins_exporting.ExportingMenu.new(root, {}),
        filePrefix: this.filenameValue ?? 'historico-idss',
      });
      const menuButton = exporting.get('menu').getPrivate('menuElement').children[0] as HTMLAnchorElement;
      menuButton.innerHTML = '<i class="pi pi-download p-1"></i>';

      // Chart
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          layout: root.verticalLayout,
        }),
      );

      chart.plotContainer.set('maskContent', false);
      (chart.seriesContainer as any)?.set?.('maskContent', false);

      // Cursor
      const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}));
      cursor.lineX.set('visible', false);

      // Title
      const title = chart.children.unshift(
        am5.Label.new(root, {
          text: TITLE_PREFIX + (this.titleText ? ' - (' + this.titleText + ')' : ''),
          fontSize: 20,
          textAlign: 'left',
          y: 0,
          x: 0,
        }),
      );

      // Logo
      chart.plotContainer.children.push(
        am5.Picture.new(root, {
          src: 'assets/images/logo-texto-preto.svg',
          width: 100,
          height: 20,
          opacity: 0.7,
          y: am5.p100,
          centerY: am5.p100,
          dx: 80,
          dy: 90,
        }),
      );

      // Add IDSS Legend
      chart.plotContainer.children.push(
        am5.Picture.new(root, {
          src: 'assets/images/legenda-grafico.gif',
          width: 200,
          height: 40,
          x: am5.p100,
          y: am5.p100,
          centerX: am5.p100,
          centerY: am5.p100,
          dy: 90,
        }),
      );

      // Create axes
      const categoryAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: 'categoria',
          tooltip: am5.Tooltip.new(root, {}),
          renderer: am5xy.AxisRendererX.new(root, {
            minGridDistance: 0, // show all categories
            cellStartLocation: 0.1,
            cellEndLocation: 0.9,
          }),
        }),
      );
      categoryAxis.get('renderer').grid.template.set('visible', false);
      categoryAxis.get('renderer').labels.template.setAll({
        textAlign: 'center',
      });

      categoryAxis.get('renderer').labels.template.adapters.add('text', (_text, target: any) => {
        const cat = target?.dataItem?.get('category');
        if (!cat) return _text;
        const year = String(cat).split('-')[0];
        const d = target?.dataItem?.dataContext as any;
        if (d?.cd_simulado == null) return year;
        const tipo = classificarColunaHistoricoIdss(d);
        return `${year}\n[fontSize:10px]${TIPO_COLUNA_HISTORICO_IDSS_LABEL[tipo]}[/]`;
      });

      categoryAxis.children.push(
        am5.Label.new(root, {
          text: 'Ano base',
          fontWeight: '800',
          x: am5.p50,
          centerX: am5.p50,
        }),
      );
      this._axis.push(categoryAxis);

      const valueAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          min: 0,
          max: 1,
          strictMinMax: true,
          tooltip: am5.Tooltip.new(root, {}),
          renderer: am5xy.AxisRendererY.new(root, {
            minGridDistance: 30,
          }),
        }),
      );
      this._axis.push(valueAxis);

      // Esconde a grade/rótulos automáticos: serão substituídos por marcações
      // fixas de 0,2 em 0,2 (faixas avaliativas do IDSS), independentes da
      // altura da tela.
      valueAxis.get('renderer').grid.template.set('visible', false);
      valueAxis.get('renderer').labels.template.set('visible', false);

      const createValueGridStep = (value: number) => {
        const rangeDataItem = valueAxis.makeDataItem({ value });
        const range = valueAxis.createAxisRange(rangeDataItem);

        range.get('grid').setAll({
          visible: true,
          strokeOpacity: 0.15,
        });

        range.get('label').setAll({
          visible: true,
          text: '{value.formatNumber("0.0")}',
          populateText: true,
        });

        return range;
      };

      [0, 0.2, 0.4, 0.6, 0.8, 1].forEach(createValueGridStep);

      const metodoAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: 'nr_ano_base', // usa ano como guia
          renderer: am5xy.AxisRendererX.new(root, {
            opposite: true,
          }),
        }),
      );
      metodoAxis.get('renderer').grid.template.set('visible', false);
      metodoAxis.get('renderer').labels.template.set('visible', false);
      this._axis.push(metodoAxis);

      const createRange = (label: string, start: string, end: string, color: string) => {
        const rangeDataItem = metodoAxis.makeDataItem({ category: start, endCategory: end });
        const range = metodoAxis.createAxisRange(rangeDataItem);

        range.get('label').setAll({
          visible: true,
          text: label,
          fontSize: 20,
          fontWeight: 'bold',
          fill: am5.color(color),
          paddingBottom: 25,
        });

        range.get('grid').setAll({
          visible: true,
          location: 0,
          stroke: am5.color(color),
          strokeOpacity: 1,
        });

        range.get('tick').setAll({
          visible: true,
          location: 0,
          length: 50,
          stroke: am5.color(color),
          strokeOpacity: 0.6,
        });

        return range;
      };

      const rangeSip = createRange('SIP', '2008', '2016', '#808080');
      const rangeTiss = createRange('TISS', '2017', '2018', '#808080');

      const createLineBackground = (color: string) =>
        am5.LinePattern.new(root, {
          color: am5.color(color),
          rotation: 45,
          width: 10,
          height: 10,
        });

      const getRangeColor = (value: number, brighten: number) => {
        if (value < 0.2) return am5.Color.brighten(am5.color('#CC0000'), brighten);
        if (value >= 0.2 && value < 0.4) return am5.Color.brighten(am5.color('#FF6600'), brighten);
        if (value >= 0.4 && value < 0.6) return am5.Color.brighten(am5.color('#FFC843'), brighten);
        if (value >= 0.6 && value < 0.8) return am5.Color.brighten(am5.color('#D8FF00'), brighten);
        return am5.Color.brighten(am5.color('#78CC00'), brighten);
      };

      const getSeriesColor = (fill: am5.Color, target: am5.Sprite | any) => {
        const value: number = target?.dataItem?.get('valueY');
        if (value != null) return getRangeColor(value, -0.2);
        return fill;
      };

      const createSeries = (value: string, name: string, centerY = false) => {
        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name,
            maskBullets: false,
            clustered: false,
            xAxis: categoryAxis,
            yAxis: valueAxis,
            categoryXField: 'categoria',
            valueYField: value,
          }),
        );

        series.columns.template.setAll({
          fillOpacity: 0.8,
          strokeOpacity: 0,
          strokeWidth: 0,
        });

        // labels numéricas
        series.bullets.push(() => {
          const shouldShowBox = centerY === true;
          return am5.Bullet.new(root, {
            locationX: 0.5,
            locationY: centerY ? 0.5 : 1,
            sprite: am5.Label.new(root, {
              text: '[bold]{valueY.formatNumber("0.0000")}[/]',
              populateText: true,
              dy: -12,
              centerX: am5.percent(50),
              centerY: am5.percent(50),
              fill: am5.color('#000'),
              background: am5.Rectangle.new(root, {
                fill: am5.color('#fff'),
                fillOpacity: shouldShowBox ? 0.5 : 0,
                strokeOpacity: 0,
              }),
            }),
          });
        });

        return series;
      };

      const createSeriesPontuacao = () => {
        const series = createSeries('vl_pontuacao', 'Pontuação', false);

        series.columns.template.set('width', am5.percent(90));
        series.columns.template.adapters.add('fill', (fill, target) => getSeriesColor(fill, target));

        const tooltip = am5.Tooltip.new(root, {
          autoTextColor: false,
          getFillFromSprite: false,
          getLabelFillFromSprite: false,
        });
        tooltip.label.setAll({ fill: am5.color('#FFFFFF') });
        tooltip.get('background').setAll({ fill: am5.color('#000000'), fillOpacity: 0.8 });
        series.set('tooltip', tooltip);

        series.columns.template.adapters.add('tooltipText', (_text, target) => {
          const data = target?.dataItem?.dataContext || {};
          const anoLabel = data['nr_ano_base'];
          let newText = `[bold]Ano-base ${anoLabel}[/]\nPontuação: {valueY.formatNumber("0.0000")}`;

          if (data['vl_pontuacao_bruta'] != null) {
            newText += '\nPontuação Bruta: {vl_pontuacao_bruta.formatNumber("0.0000")}';
          }
          if (data['vl_pontuacao_acreditacao'] != null) {
            newText += '\nAcreditação: {vl_pontuacao_acreditacao}';
          }
          return newText;
        });

        return series;
      };

      const createSeriesPontuacaoBruta = () => {
        const series = createSeries('vl_pontuacao_bruta', 'Pontuação Bruta', true);
        series.columns.template.set('width', am5.percent(60));
        series.columns.template.adapters.add('fill', (fill, target) => getSeriesColor(fill, target));
        series.columns.template.set('fillPattern', createLineBackground('#fff'));
        return series;
      };

      const createBadgeSeries = () => {
        const badgeSeries = chart.series.push(
          am5xy.LineSeries.new(root, {
            name: 'Badges',
            xAxis: categoryAxis,
            yAxis: valueAxis,
            categoryXField: 'categoria',
            valueYField: 'badgeY',
            maskBullets: false,
          }),
        );
        badgeSeries.strokes.template.setAll({ strokeOpacity: 0 });
        badgeSeries.fills.template.setAll({ fillOpacity: 0 });

        badgeSeries.bullets.push((_r, _s, dataItem) => {
          const setCursor = (cursorValue: string) => {
            (root.dom as HTMLElement).style.cursor = cursorValue;
          };

          const label = am5.Label.new(root, {
            text: '',
            centerX: am5.percent(50),
            centerY: am5.percent(50),
            populateText: false,
            textAlign: 'center',
            fontSize: 12,
            fontWeight: '700',
            fill: am5.color('#808080'),
            paddingTop: 6,
            paddingBottom: 6,
            paddingLeft: 10,
            paddingRight: 10,
            dy: -50,
            background: am5.RoundedRectangle.new(root, {
              cornerRadiusTL: 10,
              cornerRadiusTR: 10,
              cornerRadiusBL: 10,
              cornerRadiusBR: 10,
              fill: am5.color('#ffffff'),
              fillOpacity: 0.12,
              stroke: am5.color('#808080'),
              strokeOpacity: 0.4,
              strokeWidth: 1,
            }),
            interactive: true,
          });

          const bg = label.get('background') as am5.RoundedRectangle;

          label.events.on('pointerover', () => {
            setCursor('pointer');
            bg.setAll({
              fill: am5.color('#ffffff'),
              fillOpacity: 0.35,
              stroke: am5.color('#00bfeb'),
              strokeOpacity: 0.8,
            });
            label.set('fill', am5.color('#00bfeb'));
          });
          label.events.on('pointerout', () => {
            setCursor('default');
            bg.setAll({
              fill: am5.color('#ffffff'),
              fillOpacity: 0.12,
              stroke: am5.color('#808080'),
              strokeOpacity: 0.4,
            });
            label.set('fill', am5.color('#808080'));
          });
          label.events.on('click', () => setCursor('default'));

          label.adapters.add('text', () => {
            const d: any = dataItem?.dataContext as any;
            const cd = d?.cd_simulado;
            if (cd == null) return '';
            const tipo = TIPO_COLUNA_HISTORICO_IDSS_LABEL[classificarColunaHistoricoIdss(d)];
            return `${tipo}\n${cd}`;
          });

          label.adapters.add('visible', () => {
            const d: any = dataItem?.dataContext as any;
            return d?.cd_simulado != null;
          });

          const emitClick = () => {
            const d: any = dataItem?.dataContext as any;
            const cd = d?.cd_simulado;
            if (cd == null) return;

            self.simuladoClick.emit({
              cd_simulado: Number(cd),
              tp_simulado: String(d?.tp_simulado ?? ''),
              nr_ano_base: d?.nr_ano_base,
            });
          };

          label.events.on('click', emitClick);

          return am5.Bullet.new(root, {
            locationX: 0.5,
            locationY: 1,
            sprite: label,
          });
        });

        return badgeSeries;
      };

      // Create series
      const sPontuacao = createSeriesPontuacao();
      const sBruta = createSeriesPontuacaoBruta();
      const sBadges = createBadgeSeries();

      this._series.push(sPontuacao);
      this._series.push(sBruta);
      this._series.push(sBadges);

      // Create Legend
      const legend = chart.children.push(
        am5.Legend.new(root, {
          useDefaultMarker: true,
          x: am5.p50,
          y: am5.p100,
          centerX: am5.p50,
          centerY: am5.p50,
        }),
      );

      legend.markerRectangles.template.setAll({
        width: 20,
        height: 20,
        strokeWidth: 1,
        strokeOpacity: 1,
        fillPattern: createLineBackground('#000'),
        stroke: am5.color('#000'),
        // fill: am5.color('#000'),
      });

      legend.markerRectangles.template.adapters.add('fill', (fill, target: any) => {
        // legend.markerRectangles.template.adapters.add('fillPattern', (fill, target) => getLegendPattern(fill, target));
        const field = target?.dataItem?.dataContext?.get?.('valueYField');
        if (field === 'vl_pontuacao') return am5.color('#000');
        if (field === 'vl_pontuacao_bruta') return am5.color('#AAA');
        return fill;
      });

      legend.data.setAll([sPontuacao, sBruta]);

      this._root = root;
      this._chart = chart;
      this._title = title;
      this._export = exporting;
      this._rangeSip = rangeSip;
      this._rangeTiss = rangeTiss;

      if (this._pendingData) {
        this.applyData(this._pendingData);
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up chart when the component is removed
    if (this._root) this._root.dispose();
  }

  private applyData(data: any[]) {
    if (!this._root) return;

    const enriched = (data as any[]).map(d => {
      const ano = String(d?.nr_ano_base ?? '');
      const cdSim = d?.cd_simulado != null ? String(d.cd_simulado) : null;

      return {
        ...d,
        categoria: cdSim ? `${ano}-${cdSim}-${d?.tp_simulado ?? ''}` : ano,
        badgeY: 1,
      };
    });

    const sipData = enriched.filter(d => d.tp_metodo === 'SIP');
    const tissData = enriched.filter(d => d.tp_metodo === 'TISS');

    if (sipData.length > 0) {
      this._rangeSip.setAll({
        category: sipData[0]['categoria'],
        endCategory: sipData[sipData.length - 1]['categoria'],
        visible: true,
      });
    } else {
      this._rangeSip.set('visible', false);
    }

    if (tissData.length > 0) {
      this._rangeTiss.setAll({
        category: tissData[0]['categoria'],
        endCategory: tissData[tissData.length - 1]['categoria'],
        visible: true,
      });
    } else {
      this._rangeTiss.set('visible', false);
    }

    this._series.forEach(series => series.data.setAll(enriched));
    this._axis.forEach(axis => axis.data.setAll(enriched));
  }

  @Input()
  set data(data: any) {
    const list = Array.isArray(data) ? data : null;
    // console.log('[Hist] data', data);

    this._pendingData = list;

    if (list && this._root) {
      this.applyData(list);
    }
  }

  @Input()
  set title(title: string) {
    if (!this._title) {
      this.titleText = title;
      return;
    }

    this._title.set('text', TITLE_PREFIX + (title ? ' - (' + title + ')' : ''));
  }

  @Input()
  set filename(filename: string) {
    if (!this._root) {
      this.filenameValue = filename;
      return;
    }

    this._export?.set('filePrefix', filename);
  }

  @Input()
  set simuladoLabel(_label: string | null) {}
}
